// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import { useEffect } from 'react';
import statsTracker from '../utils/game/StatsTracker';
import { ProgressionSystem } from '../utils/game/ProgressionSystem';
import contextualTutorial from '../utils/game/ContextualTutorial';
import EventBus, { EVENT_TYPES } from '../utils/system/EventBus';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';
import * as StoryArcManager from '../utils/game/StoryArcManager';

/**
 * Wires up all global EventBus listeners for missions, payday,
 * rank-up tracking, and contextual tutorial hints.
 */
export function useGameEvents({ success, error, setCurrentHint }) {
  // Mission completion
  useEffect(() => {
    const handleMissionComplete = (data) => {
      statsTracker.recordMissionComplete();
      statsTracker.recordCreditsEarned(data.credits);
      success(
        `🎉 MISSION COMPLETE!\n${data.mission.title}\n💰 +${data.credits} Credits | ⭐ +${data.reputation} Reputation`,
        { duration: 8000 }
      );
    };

    EventBus.on(EVENT_TYPES.MISSION_COMPLETE, handleMissionComplete);
    return () => EventBus.off('MISSION_COMPLETE', handleMissionComplete);
  }, [success]);

  // Payday notifications

  // Story arc objective checking
  useEffect(() => {
    const processArcResults = (matches) => {
      matches.forEach(({ arcId }) => {
        const result = StoryArcManager.advanceStep(arcId);
        if (!result) return;
        if (result.arcCompleted) {
          success(`🏆 STORY ARC COMPLETE: ${result.arc.title}\n💰 +${result.rewards.credits} CR | ⭐ +${result.rewards.reputation} REP`, { duration: 10000 });
        } else {
          const nextStep = result.arc.steps[result.arc.currentStep];
          success(`📖 ${result.arc.title}: "${result.stepCompleted.title}" complete!\n💰 +${result.rewards.credits} CR\nNext: ${nextStep.title} — ${nextStep.targetSystem}`, { duration: 8000 });
        }
      });
    };

    const handleOrbit = (data) => {
      const systemName = data?.systemName || data?.name;
      if (systemName) processArcResults(StoryArcManager.checkTravelObjective(systemName));
    };
    const handleScan = (data) => {
      const systemName = data?.systemName || data?.name;
      if (systemName) processArcResults(StoryArcManager.checkScanObjective(systemName));
    };
    const handleCombatWin = () => {
      processArcResults(StoryArcManager.checkCombatObjective());
    };

    EventBus.on(EVENT_TYPES.ENTER_ORBIT, handleOrbit);
    EventBus.on(EVENT_TYPES.SCAN_COMPLETE, handleScan);
    EventBus.on(EVENT_TYPES.COMBAT_COMPLETE, handleCombatWin);
    return () => {
      EventBus.off('ENTER_ORBIT', handleOrbit);
      EventBus.off('SCAN_COMPLETE', handleScan);
      EventBus.off('COMBAT_COMPLETE', handleCombatWin);
    };
  }, [success]);

  useEffect(() => {
    const handlePayday = ({ amount, crewCount, success: paid, shortage }) => {
      if (paid) {
        success(`💰 Payday: ${amount} CR paid to ${crewCount} crew members`, { duration: 5000 });
      } else {
        error(`⚠️ PAYDAY MISSED! Need ${amount} CR, short by ${shortage} CR. Crew morale dropping!`, { duration: 8000 });
      }
    };

    const unsubscribe = EventBus.on(EVENT_TYPES.PAYDAY, handlePayday);
    return unsubscribe;
  }, [success, error]);

  // Stats tracking and rank-up checks
  useEffect(() => {
    const checkRankUp = () => {
      const oldRank = localStorage.getItem(STORAGE_KEYS.CURRENT_RANK) || 'Cadet';
      const stats = statsTracker.getStats();
      const newRank = ProgressionSystem.getRank(stats).rank;

      if (newRank !== oldRank) {
        localStorage.setItem(STORAGE_KEYS.CURRENT_RANK, newRank);
        const rankUp = ProgressionSystem.checkRankUp(
          { ...stats, currentRank: oldRank },
          stats
        );

        if (rankUp.rankedUp) {
          success(
            `🎖️ RANK UP!\n${rankUp.oldRank} → ${rankUp.newRank}\n` +
            `Rewards: ${rankUp.rewards.credits}¢, ${rankUp.rewards.reputation} Rep`,
            { duration: 10000 }
          );
        }
      }
    };

    const handleSystemVisit = (systemId) => { statsTracker.visitSystem(systemId); checkRankUp(); };
    const handleCombatWin = () => { statsTracker.recordCombatWin(); checkRankUp(); };
    const handleCreditsEarned = (amount) => { statsTracker.recordCreditsEarned(amount); checkRankUp(); };

    EventBus.on(EVENT_TYPES.SYSTEM_VISITED, handleSystemVisit);
    EventBus.on(EVENT_TYPES.COMBAT_VICTORY, handleCombatWin);
    EventBus.on(EVENT_TYPES.CREDITS_EARNED, handleCreditsEarned);

    return () => {
      EventBus.off('SYSTEM_VISITED', handleSystemVisit);
      EventBus.off('COMBAT_VICTORY', handleCombatWin);
      EventBus.off('CREDITS_EARNED', handleCreditsEarned);
    };
  }, [success]);

  // Contextual tutorial hints
  useEffect(() => {
    const showHint = (triggerId) => {
      const hint = contextualTutorial.shouldShow(triggerId);
      if (hint) setCurrentHint(hint);
    };

    const handleWarpEngaged = () => showHint('beforeFirstWarp');
    const handleCombatStart = () => showHint('onCombatStart');
    const handleFirstOrbit = () => {
      showHint('onFirstOrbit');
      setTimeout(() => showHint('afterFirstOrbit'), 5000);
    };
    const handleLowFuel = () => showHint('onLowFuel');
    const handleMissionAvailable = () => showHint('onFirstMissionAvailable');
    const handleAlienEncounter = () => showHint('onFirstAlienEncounter');
    const handleRankProgress = () => showHint('onFirstRankProgress');

    EventBus.on(EVENT_TYPES.WARP_ENGAGED, handleWarpEngaged);
    EventBus.on(EVENT_TYPES.COMBAT_START, handleCombatStart);
    EventBus.on(EVENT_TYPES.FIRST_ORBIT, handleFirstOrbit);
    EventBus.on(EVENT_TYPES.LOW_FUEL, handleLowFuel);
    EventBus.on(EVENT_TYPES.MISSION_AVAILABLE, handleMissionAvailable);
    EventBus.on(EVENT_TYPES.ALIEN_ENCOUNTER, handleAlienEncounter);
    EventBus.on(EVENT_TYPES.RANK_PROGRESS, handleRankProgress);

    return () => {
      EventBus.off('WARP_ENGAGED', handleWarpEngaged);
      EventBus.off('COMBAT_START', handleCombatStart);
      EventBus.off('FIRST_ORBIT', handleFirstOrbit);
      EventBus.off('LOW_FUEL', handleLowFuel);
      EventBus.off('MISSION_AVAILABLE', handleMissionAvailable);
      EventBus.off('ALIEN_ENCOUNTER', handleAlienEncounter);
      EventBus.off('RANK_PROGRESS', handleRankProgress);
    };
  }, [setCurrentHint]);
}
